import { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { merge } from "ts-deepmerge";

export type FormCompProps = {
  className?: string;
  placeholder?: string;
  name: string;
  required?: boolean;
  readOnly?: boolean;
  disabled?: boolean;
  value: string;
  onChange: (value?: string) => void;
  onBlur: () => void;
}

type DeepPartial<T> = { [K in keyof T]?: T[K] extends object ? DeepPartial<T[K]> : T[K] };
type ByKey<T> = Record<string, T>;
type MergedFormElement = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
type Validator = { func: (val: any) => boolean, error: string }

type RegisterOptions = { required?: string, readOnly?: boolean, validators?: ByKey<Validator>; }
type RegisterProps<T> = {
  name: string;
  required?: boolean;
  readOnly?: boolean;
  disabled?: boolean;
  value: T | "";
  onChange: (eventOrValue: ChangeEvent<MergedFormElement> | T | undefined) => void;
  onBlur: () => void;
};

type SubmitOptions = { resetValues?: boolean, resetStateSubmitTimer?: number }
type FormState = {
  isDirty: boolean;
  isValid: boolean;
  dirtyFields: ByKey<{ type: string, error: string }>;
  submit: {
    inProcess: boolean;
    succeeded: boolean;
    finished: boolean;
  }
}

// todo: check if work with array
const getByPath = <T extends object, R = unknown>(obj: T, path: string): R | undefined =>
  path.split(".").reduce<any>((acc, key) => acc?.[key], obj || {});

// todo: check if work with array
const setByPath = <T extends object>(obj: T, path: string, value: any): void => {
  const keys = path.split(".");
  const last = keys.pop()!;
  if (!last) return;
  const target = keys.reduce<any>((acc, key) => {
    if (acc[key] === undefined) acc[key] = {};
    return acc[key];
  }, obj);
  target[last] = value;
}

const flatten = (obj: any, parentKey: string = ""): ByKey<any | undefined> => {
  if (obj === null)
    return parentKey ? { [parentKey]: obj } : {};
  if (Array.isArray(obj)) {
    return obj.reduce((acc, cur, index) => {
      const fullKey = parentKey ? `${parentKey}.${index}`: `${index}`;
      return {
        ...acc,
        ...(typeof cur === "object") && { ...flatten(cur, fullKey) },
        ...(typeof cur !== "object") && { [fullKey]: cur }
      };
    }, {})
  }
  if (typeof obj === "object") {
    return Object.entries(obj).reduce((acc, [ key, cur ]) => {
      const fullKey = parentKey ? `${parentKey}.${key}` : key;
      return {
        ...acc,
        ...(typeof cur === "object") && { ...flatten(cur, fullKey) },
        ...(typeof cur !== "object") && { [fullKey]: cur }
      }
    }, {});
  }
  return parentKey ? { [parentKey]: obj } : {};
}

const isChangeEvent = (val: unknown): val is ChangeEvent<MergedFormElement> =>
  typeof val === "object" && val !== null && "nativeEvent" in val;


export const useForm = <TValue>(defaultValue?: DeepPartial<TValue>) => {

  const [ init, setInit ] = useState<boolean>(true);
  const [ submitting, setSubmitting ] = useState<boolean>(false);
  const [ failed, setFailed ] = useState<boolean>(false);
  const [ succeeded, setSucceeded ] = useState<boolean>(false);

  const [ values, setValues ] = useState<DeepPartial<TValue>>(defaultValue || {});
  const [ touched, setTouched ] = useState<ByKey<boolean>>({});
  const [ validators, setValidators ] = useState<ByKey<ByKey<Validator>>>({})
  const [ errors, setErrors ] = useState<ByKey<ByKey<string>>>({});


  // logic

  const registerField = <T,>(name: string, opt?: RegisterOptions) => {
    const current = {
      ...(!!opt?.required) && {
        required: {
          func: (val?: any) => typeof val === "boolean" ? val : typeof val !== "undefined",
          error: opt.required
        }
      },
      ...opt?.validators
    };

    if (!(name in validators)) {
      setValidators(prev => ({ ...prev, [name]: current }));
      checkValidators(name, undefined, current);
    }

    init && setInit(false);
    return (getByPath(values || {}, name)) as T;
  }

  const setFieldValue = (name: string, value: any) => {
    setValues(prev => {
      const next = structuredClone(prev);
      setByPath(next, name, value === "" ? undefined : value);
      return next;
    });
  }

  const checkValidators = (name: string, value: any, fieldValidators?: ByKey<Validator>) => {
    const fieldCheck = Object.entries(fieldValidators || {}).reduce<ByKey<string>>((err, [ key, validator ]) => ({
      ...err,
      ...(!validator.func(value) && { [key]: validator.error })
    }), {});
    setErrors(prev => {
      const { [name]: current, ...others } = prev;
      return { ...others, ...(Object.values(fieldCheck).length > 0) && { [name]: fieldCheck } };
    });
  }

  // effects

  // refresh errors state on values changes
  useEffect(() => {
    Object.entries(validators)
          .forEach(([ name, validators ]) => checkValidators(name, getByPath(values, name), validators))
  }, [ values ]);


  // dom interactions

  const handleChange = (e: ChangeEvent<MergedFormElement>) => {
    const { name } = e.target;
    const value = e.target.getAttribute("type") === "checkbox"
      ? (e.target as HTMLInputElement).checked
      : e.target.value;
    setFieldValue(name, value);
  }

  const markTouched = (name: string) => {
    setTouched(prev => ({ ...prev, [name]: true }));
  }

  const handleSubmit = (process: (data: TValue) => Promise<void>, options?: SubmitOptions) =>
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setSubmitting(true);
      failed && setFailed(false);
      succeeded && setSucceeded(false);
      await process(values as TValue)
        .then(() => {
          setSucceeded(true);
          !!options?.resetValues && patch(true);
        })
        .catch(() => setFailed(true))
        .finally(() => {
          setSubmitting(false);
          !!options?.resetStateSubmitTimer && setTimeout(() => {
            setSubmitting(false);
            setFailed(false);
            setSucceeded(false);
          }, options.resetStateSubmitTimer);
        });
    }

  // result

  const register = <T = string>(name: string, opt?: RegisterOptions): RegisterProps<NoInfer<T>> => {
    const value = registerField<T>(name, opt);
    return {
      name,
      ...(!!opt?.required) && { required: true },
      ...(!!opt?.readOnly) && { readOnly: opt.readOnly, disabled: opt.readOnly },
      value: typeof value === "undefined" ? "" : value,
      onChange: (event: ChangeEvent<MergedFormElement> | T | undefined) => {
        if (isChangeEvent(event)) handleChange(event);
        else setFieldValue(name, event);
      },
      onBlur: () => markTouched(name)
    }
  }

  const patch = (reset: boolean = false, val?: DeepPartial<TValue>) => {
    setValues(prev => reset ? val || defaultValue || {} : merge(prev, val || {}));
    setTouched({});
  }

  const formState: FormState = {
    isDirty: init || Object.values(touched).length === 0,
    isValid: init ? false : Object.values(errors).length === 0,
    dirtyFields: Object
      .entries(errors)
      .filter(([ name, _ ]) => Object.keys(touched).includes(name))
      .reduce((acc, [ name, errors ]) => {
        const [ type, error ] = Object.entries(errors)[0];
        return { ...acc, [name]:  { type, error }};
        }, {}),
    submit: {
      inProcess: submitting,
      finished: !submitting && (succeeded || failed),
      succeeded: !submitting && succeeded
    }
  };

  return { register, currentValue: values, handleSubmit, patch, formState };

}

export const validators = {
  emailFormat: (error: string) => ({
    format: {
      func: (val?: string) => !!val && /^[a-zA-Z0-9][-a-zA-Z0-9._]+@([-a-zA-Z0-9]+\.)+[a-zA-Z]{2,5}$/.test(val),
      error
    }
  }),
  phoneFormat: (error: string) => ({
    format: {
      func: (val?: string) => !!val && /^\+(?:[0-9]\s?){6,14}[0-9]$/.test(val),
      error
    }
  }),
  minLength: (min: number, error: string) => ({
    length: {
      func: (val?: string) => !!val && val?.trim().length >= min,
      error
    }
  })
}
